import React from 'react';
import { Linking, View, Text } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Provider as PaperProvider } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { supabase } from './src/services/supabase';
import { Session } from '@supabase/supabase-js';
import { theme } from './src/theme';

// Telas
import { ConsultaCNPJ } from './src/screens/ConsultaCNPJ';
import { Cobrancas } from './src/screens/Cobrancas';
import { Automacoes } from './src/screens/Automacoes';
import { Login, ForgotPassword } from './src/screens/Auth';
import { ListaTitulos } from './src/screens/ListaTitulos';
import { ListaBoletos } from './src/screens/ListaBoletos';
import { ModeloMensagemStack } from './src/screens/ModeloMensagem';
import { ScoreClientes } from './src/screens/ScoreClientes';
import { Relatorios } from './src/screens/Relatorios';
import { AgendamentoCobranca } from './src/screens/AgendamentoCobranca';
import { AgendamentoMultiplo } from './src/screens/AgendamentoMultiplo';
import ScoreScreen from './src/screens/score/ScoreScreen';
import DetalheClienteScreen from './src/screens/score/DetalheClienteScreen';
import ListaClientesScreen from './src/screens/score/ListaClientesScreen';
import DetalhesCNPJPage from './src/screens/DetalhesCNPJPage';
import { AdministracaoStack } from './src/screens/Administracao';
import { GerenciarInfraestruturaChatbot } from './src/screens/Administracao/GerenciarInfraestruturaChatbot';
import { WhatsAppStack } from './src/screens/WhatsApp';
import ChatwootDashboard from './src/screens/ChatwootDashboard';

console.warn(' ARQUIVO App.tsx CARREGADO ');

type AuthStackParamList = {
  Login: undefined;
  ForgotPassword: undefined;
};

type RootStackParamList = {
  Auth: undefined;
  DrawerHome: undefined;
  ListaTitulos: { cliente: any };
  cliente: any;
  AgendamentoCobranca: { titulo: any };
  AgendamentoMultiplo: { titulo: any };
  DetalheCliente: { cliente: any };
  ListaClientes: undefined;
  Clientes: undefined;
  DetalhesCNPJ: undefined;
  Menu: undefined;
  ChatwootDashboard: undefined;
};

const Drawer = createDrawerNavigator();
const Stack = createNativeStackNavigator<RootStackParamList>();
const AuthStack = createNativeStackNavigator<AuthStackParamList>();

// Componente simples para telas em desenvolvimento
const EmptyScreen = () => {
  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
      <Text>Em desenvolvimento</Text>
    </View>
  );
};

const drawerScreens = [
  {
    name: 'Dashboard',
    component: ChatwootDashboard,
    icon: 'dashboard'
  },
  {
    name: 'Cobranças',
    component: Cobrancas,
    icon: 'monetization-on'
  },
  {
    name: 'Boletos',
    component: ListaBoletos,
    icon: 'receipt'
  },
  {
    name: 'Score (em desenv.)',
    component: ScoreScreen,
    icon: 'score'
  },
  {
    name: 'Consulta CNPJ',
    component: () => {
      Linking.openURL('https://consulta-cliente-rose.vercel.app/');
      return null;
    },
    icon: 'business'
  },
  {
    name: 'Relatórios (em desenv.)',
    component: Relatorios,
    icon: 'description'
  },
  {
    name: 'Automações',
    component: Automacoes,
    icon: 'settings-input-component'
  },
  {
    name: 'Modelos de Mensagem',
    component: ModeloMensagemStack,
    icon: 'message'
  },
  {
    name: 'Instâncias WhatsApp',
    component: WhatsAppStack,
    icon: 'chat'
  },
  {
    name: 'Administração',
    component: AdministracaoStack,
    icon: 'admin-panel-settings',
    adminOnly: true
  },
  {
    name: 'Configurações',
    component: EmptyScreen,
    icon: 'settings'
  },
  {
    name: 'Perfil',
    component: EmptyScreen,
    icon: 'person'
  },
  {
    name: 'Logoff',
    component: () => {
      // Usando o método adequado de logout
      React.useEffect(() => {
        const handleLogout = async () => {
          await supabase.auth.signOut();
        };
        handleLogout();
      }, []);
      return null;
    },
    icon: 'exit-to-app'
  }
];

function DrawerNavigator() {
  const [isAdmin, setIsAdmin] = React.useState(false);
  
  React.useEffect(() => {
    // Verificar se o usuário é admin
    const checkAdminStatus = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data } = await supabase
          .from('usuarios')
          .select('admin')
          .eq('id', user.id)
          .single();
          
        setIsAdmin(data?.admin || false);
      } catch (error) {
        console.error('Erro ao verificar status de admin:', error);
      }
    };
    
    checkAdminStatus();
  }, []);
  
  // Filtrar as telas com base no status de admin
  const filteredScreens = drawerScreens.filter(screen => 
    !screen.adminOnly || (screen.adminOnly && isAdmin)
  );
  
  return (
    <Drawer.Navigator
      initialRouteName="Cobranças"
      screenOptions={{
        headerStyle: {
          backgroundColor: '#f5f5f5',
        },
        headerTintColor: '#000',
        headerTitleStyle: {
          fontWeight: 'bold',
        },
        drawerActiveTintColor: theme.colors.primary,
        drawerInactiveTintColor: theme.colors.onSurfaceVariant,
        drawerStyle: {
          backgroundColor: '#fff',
          width: 280,
        },
        drawerType: 'front',
      }}
    >
      {filteredScreens.map((screen) => (
        <Drawer.Screen
          key={screen.name}
          name={screen.name}
          component={screen.component}
          options={{
            drawerIcon: ({ color }) => (
              <Icon name={screen.icon} size={24} color={color} />
            ),
          }}
        />
      ))}
    </Drawer.Navigator>
  );
}

function AuthNavigator() {
  return (
    <AuthStack.Navigator screenOptions={{ headerShown: false }}>
      <AuthStack.Screen name="Login" component={Login} />
      <AuthStack.Screen name="ForgotPassword" component={ForgotPassword} />
    </AuthStack.Navigator>
  );
}

function RootNavigator() {
  return (
    <Stack.Navigator>
      <Stack.Screen
        name="DrawerHome"
        component={DrawerNavigator}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="ListaTitulos"
        component={ListaTitulos}
        options={{
          title: 'Lista de Títulos',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="AgendamentoCobranca"
        component={AgendamentoCobranca}
        options={{
          title: 'Agendamento de Cobrança',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="AgendamentoMultiplo"
        component={AgendamentoMultiplo}
        options={{
          title: 'Agendamento Múltiplo',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="DetalheCliente"
        component={DetalheClienteScreen}
        options={{
          title: 'Detalhes do Cliente',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="ListaClientes"
        component={ListaClientesScreen}
        options={{
          title: 'Lista de Clientes',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="Clientes"
        component={ListaClientesScreen}
        options={{
          title: 'Clientes',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="DetalhesCNPJ"
        component={DetalhesCNPJPage}
        options={{
          title: 'Detalhes CNPJ',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
      <Stack.Screen
        name="Menu"
        component={EmptyScreen}
        options={{
          title: 'Menu',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
          <Stack.Screen
        name="ChatwootDashboard"
        component={ChatwootDashboard}
        options={{
          title: 'Dashboard Chatwoot',
          headerStyle: {
            backgroundColor: '#f5f5f5',
          },
          headerTintColor: '#000',
          headerTitleStyle: {
            fontWeight: 'bold',
          },
        }}
      />
    </Stack.Navigator>
  );
}

export const RefreshContext = React.createContext({
  refreshKey: 0,
  triggerRefresh: () => {}
});

export default function App() {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const triggerRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  if (loading) {
    return null; // Ou um componente de loading global
  }

  return (
    <PaperProvider theme={theme}>
      <RefreshContext.Provider value={{ refreshKey, triggerRefresh }}>
        <NavigationContainer>
          {!session || !session.user ? (
            <AuthNavigator />
          ) : (
            <RootNavigator />
          )}
        </NavigationContainer>
      </RefreshContext.Provider>
    </PaperProvider>
  );
}
